/**
 * Amplitude Analytics service for BamakoFuel Web
 * Mirrors the mobile app analytics structure for consistent tracking
 */

import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';

interface StationData {
  id: string;
  name: string;
  brand?: string;
  fuel_types?: string[];
}

class AmplitudeService {
  private isInitialized = false;
  private readonly apiKey = '69c4842b8d25d1cb21684c60c2e9fa01';
  private readonly serverZone = 'EU';

  constructor() {
    this.init();
  }

  async init() {
    try {
      await amplitude.init(this.apiKey, undefined, {
        serverZone: this.serverZone as 'EU',
        logLevel: amplitude.Types.LogLevel.Warn,
        defaultTracking: {
          sessions: true,
          pageViews: true,
          formInteractions: false,
          fileDownloads: false,
        },
        minIdLength: 1,
        trackingOptions: {
          ipAddress: false,
          platform: true,
          language: true,
        },
      }).promise;

      // Add session replay plugin with same settings as mobile
      try {
        const sessionReplay = sessionReplayPlugin({
          sampleRate: 0.1, // Record 10% of sessions (same as mobile)
        });

        await amplitude.add(sessionReplay).promise;
      } catch (sessionReplayError) {
        // Continue without session replay if it fails
      }

      this.isInitialized = true;

      // Send initialization event
      setTimeout(() => {
        this.track('Analytics Initialized', {
          initialization_mode: 'with_session_replay',
          platform: 'web',
          session_replay_enabled: true,
        });
      }, 1000);

    } catch (error) {
      this.isInitialized = false;
    }
  }

  // Set user properties
  setUserId(userId: string) {
    if (this.isInitialized) {
      amplitude.setUserId(userId);
    }
  }

  setUserProperties(properties: Record<string, any>) {
    if (this.isInitialized) {
      amplitude.identify(properties);
    }
  }

  // Track events
  track(eventName: string, properties: Record<string, any> = {}) {
    if (this.isInitialized) {
      try {
        const enrichedProperties = {
          ...this.sanitizeProperties(properties),
          platform: 'web',
          timestamp: new Date().toISOString(),
        };

        amplitude.track(eventName, enrichedProperties);
      } catch (error) {
        // Silent fail
      }
    }
  }

  // Helper to sanitize properties and ensure proper types
  private sanitizeProperties(properties: Record<string, any>): Record<string, any> {
    const sanitized: Record<string, any> = {};
    for (const [key, value] of Object.entries(properties)) {
      if (value === 'true') {
        sanitized[key] = true;
      } else if (value === 'false') {
        sanitized[key] = false;
      } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null) {
        sanitized[key] = value;
      } else {
        sanitized[key] = String(value);
      }
    }
    return sanitized;
  }

  // Page tracking (equivalent to screen tracking in mobile)
  trackPage(pageName: string, properties: Record<string, any> = {}) {
    this.track('Page Viewed', {
      page_name: pageName,
      ...properties,
    });
  }

  // Common fuel app events - matching mobile app methods
  trackStationViewed(station: StationData) {
    this.track('Station Viewed', {
      station_id: station.id,
      station_name: station.name,
      station_brand: station.brand,
      fuel_types: station.fuel_types?.join(', ') || 'unknown',
    });
  }

  trackStationFavorited(station: StationData, isFavorited: boolean) {
    this.track(isFavorited ? 'Station Added to Favorites' : 'Station Removed from Favorites', {
      station_id: station.id,
      station_name: station.name,
      station_brand: station.brand,
    });
  }

  trackFuelPriceViewed(station: StationData, fuelType: string, price: number) {
    this.track('Fuel Price Viewed', {
      station_id: station.id,
      station_name: station.name,
      fuel_type: fuelType,
      price: price,
      currency: 'CFA',
    });
  }

  trackReportSubmitted(reportType: string, stationId: string) {
    this.track('Report Submitted', {
      report_type: reportType,
      station_id: stationId,
    });
  }

  trackSearch(query: string, resultsCount: number) {
    this.track('Search Performed', {
      search_query: query,
      results_count: resultsCount,
    });
  }

  trackAppOpened() {
    this.track('App Opened');
  }

  // Web-specific events
  trackMapInteraction(action: string, properties: Record<string, any> = {}) {
    this.track('Map Interaction', {
      action,
      ...properties,
    });
  }

  trackFilterUsed(filterType: string, filterValue: any) {
    this.track('Filter Used', {
      filter_type: filterType,
      filter_value: String(filterValue),
    });
  }

  trackModalOpened(modalName: string) {
    this.track('Modal Opened', {
      modal_name: modalName,
    });
  }

  trackModalClosed(modalName: string) {
    this.track('Modal Closed', {
      modal_name: modalName,
    });
  }

  trackDownloadBannerShown(platform: string) {
    this.track('Download Banner Shown', {
      banner_platform: platform,
    });
  }

  trackDownloadBannerClicked(platform: string) {
    this.track('Download Banner Clicked', {
      banner_platform: platform,
    });
  }

  trackDownloadBannerDismissed(platform: string) {
    this.track('Download Banner Dismissed', {
      banner_platform: platform,
    });
  }

  // Critical user actions
  trackCriticalAction(actionName: string, properties: Record<string, any> = {}) {
    this.track('Critical Action', {
      action_name: actionName,
      replay_priority: 'high',
      ...properties,
    });
  }

  // Session Replay specific methods (matching mobile app)
  startSessionReplay() {
    if (this.isInitialized) {
      try {
        this.track('Session Replay Started', { manual: true });
      } catch (error) {
        // Silent fail
      }
    }
  }

  stopSessionReplay() {
    if (this.isInitialized) {
      try {
        this.track('Session Replay Stopped', { manual: true });
      } catch (error) {
        // Silent fail
      }
    }
  }

  // Test method to verify Amplitude is working
  testAmplitude() {
    this.track('Amplitude Test Event', {
      test_timestamp: new Date().toISOString(),
      test_platform: 'web',
      test_version: '1.0.0',
    });

    return {
      isInitialized: this.isInitialized,
      apiKey: this.apiKey,
      serverZone: this.serverZone,
    };
  }
}

// Create singleton instance
const amplitudeAnalytics = new AmplitudeService();

export default amplitudeAnalytics;
export { AmplitudeService };